<template>
    <div>
        <div class="relative mb-6">
            <h6 class="text-slate-800 text-sm font-medium mb-2">
                Hotel reward program
            </h6>
            <div class="flex items-center justify-between">
                <fieldset class="w-full">
                    <div v-if="loadingBrands" class="flex items-center">
                        <span class="text-gray-500 text-sm">
                            <i class="fal fa-spinner-third fa-spin mr-1"></i>
                            Getting hotel brands ...
                        </span>
                    </div>
                    <ul
                        v-if="!loadingBrands"
                        class="grid grid-cols-1 gap-4 lg:grid-cols-3"
                    >
                        <li
                            class="rounded-lg mb-2 col-span-1 relative"
                            v-for="option in brands"
                            :class="{
                                'border-2 border-orange-600':
                                    option.slug === brandChosen,
                                'border border-slate-200':
                                    option.slug !== brandChosen,
                            }"
                            :key="option.slug"
                        >
                            <label
                                class="flex items-center justify-center min-h-20 px-6 py-4"
                            >
                                <input
                                    name="brand"
                                    v-model="brandChosen"
                                    type="radio"
                                    class="border-gray-300 text-orange focus:ring-orange-400 absolute top-2 right-2"
                                    :value="option.slug"
                                    @change="selectBrand(option)"
                                />

                                <span class="sr-only">
                                    {{ option.name }}
                                </span>
                                <img
                                    class="inline w-20 h-auto"
                                    :alt="option.slug"
                                    :src="`/images/${option.image}`"
                                />
                            </label>
                        </li>
                    </ul>
                </fieldset>
            </div>
        </div>
        <div class="relative">
            <label class="relative">
                <span
                    class="block text-slate-800 text-sm font-medium mb-2"
                    :class="{ 'text-gray-400': !brandChosen }"
                >
                    Hotel property
                </span>
                <div
                    class="relative rounded border border-slate-200 flex items-center pr-2"
                    :class="{
                        'border-slate-800 ring-0':
                            inputFocused || (inputHover && brandChosen),
                    }"
                    @mouseover="() => handleHover(true)"
                    @mouseleave="() => handleHover(false)"
                >
                    <icon
                        icon="hotel"
                        svg-class="fill-none"
                        path-class="stroke-slate-800"
                        class="absolute left-4 top-3 z-10"
                        :class="{ 'opacity-50': !brandChosen }"
                    />

                    <input
                        type="text"
                        name="properties_query"
                        id="properties-query"
                        v-model="propertiesQuery"
                        class="border-none rounded w-full bg-white text-base inline-block px-10 text-gray-900 placeholder:text-sm placeholder:text-gray-400 focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        :placeholder="
                            brandChosen
                                ? 'Search hotel by name or location'
                                : 'Choose the Hotel reward program above first'
                        "
                        v-debounce="500"
                        @input="propertiesInput"
                        :disabled="!brandChosen"
                        @click="checkActive()"
                        @focus="() => updateFocus(true)"
                        @blur="() => updateFocus(false)"
                    />
                    <ClearInputButton
                        @clear="clearPropertySearch"
                        class=""
                        v-if="propertiesQuery"
                    />
                </div>
                <span
                    class="text-gray-500 absolute right-9 top-10 z-10"
                    v-if="loadingProperties"
                >
                    <i class="fal fa-spinner-third fa-spin mr-1"></i>
                </span>
            </label>
            <Errors :errors="errors" v-if="errors.length > 0" class="mt-3" />
            <transition name="fade">
                <div class="pt-1" v-if="properties.length > 0">
                    <ul
                        class="w-full max-h-40 overflow-y-auto p-2 bg-white rounded-lg shadow"
                    >
                        <li
                            v-for="(prop, pIndex) in properties"
                            :key="`property-${pIndex}`"
                            @click="selectProperty(prop)"
                            class="w-full cursor-pointer p-2 hover:bg-slate-100 transition-colors rounded-md flex items-center justify-between"
                            role="button"
                        >
                            <span class="text-sm">{{ prop.name }}</span>
                        </li>
                    </ul>
                </div>
            </transition>
        </div>
    </div>
</template>
<script>
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ClearInputButton from '@/components/ui/clear-input-button/clear-input-button.vue';

export default {
    components: {
        ClearInputButton,
        Errors,
        Icon,
    },
    props: {
        type: {
            type: String,
            default: 'hotels',
        },
    },
    data: () => ({
        loadingBrands: false,
        loadingProperties: false,
        brandChosen: null,
        propertyChosen: false,
        propertiesQuery: null,
        propertySelected: false,
        brands: [],
        properties: [],
        errors: [],
        inputFocused: false,
        inputHover: false,
    }),
    computed: {},
    methods: {
        propertiesInput() {
            if (
                this.brandChosen &&
                !this.propertySelected &&
                this.propertiesQuery &&
                this.propertiesQuery.length > 2
            ) {
                this.loadingProperties = true;
                ApiService.apiCall('/hotels/properties-with-brand', 'get', {
                    search_query: this.propertiesQuery,
                    brand: this.brandChosen,
                })
                    .then((response) => {
                        this.properties = response.data;
                        this.loadingProperties = false;
                    })
                    .catch(() => {
                        this.loadingProperties = true;
                    });
            } else {
                this.properties = [];
                this.loadingProperties = false;
            }
        },
        selectBrand(brand) {
            this.brandChosen = brand.slug;
            this.properties = [];
            this.propertiesQuery = null;
            this.propertySelected = false;
        },
        selectProperty(property) {
            this.propertySelected = true;
            this.propertiesQuery = property.name;
            this.propertyChosen = property;
            setTimeout(() => {
                this.properties = [];
            }, 100);
            this.$emit('propertyChanged', property);
        },
        checkActive() {
            this.errors = [];
            if (!this.brandChosen) {
                this.errors.push({
                    message: 'Please select a Hotel Brand first',
                });
            }
        },
        clearPropertySearch() {
            this.propertiesQuery = null;
            this.properties = [];
            this.propertyChosen = false;
            this.propertySelected = false;
            this.$emit('propertyChanged', null);
        },
        updateFocus(val) {
            this.inputFocused = val;
        },
        handleHover(val) {
            this.inputHover = val;
        },
    },
    mounted() {
        const storedHotelsBrands =
            JSON.parse(localStorage.getItem('hotel-brands')) || [];
        if (storedHotelsBrands.length === 0) {
            this.loadingBrands = true;
        } else {
            this.brands = storedHotelsBrands;
        }

        ApiService.apiCall('/hotels/groups', 'get')
            .then((response) => {
                this.brands = response.data.data;
                localStorage.setItem(
                    'hotel-brands',
                    JSON.stringify(this.brands)
                );
                this.loadingBrands = false;
            })
            .catch((e) => {
                console.log(e.message);
                this.loadingBrands = false;
            });
    },
};
</script>
